import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, TOKEN_KEY } from '../lib/api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauthenticated, object = signed in

  const refresh = useCallback(async () => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        console.warn('auth/me failed:', err?.message);
      }
      sessionStorage.removeItem(TOKEN_KEY);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if we're returning from the Emergent Google Auth redirect,
    // the URL fragment contains `#session_id=...`. Skip the /auth/me check
    // here — the AuthCallback screen owns the exchange and will populate
    // the user once the cookie is set. Otherwise checkAuth() races the
    // callback and we get a spurious 401 (per Emergent Auth playbook).
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    sessionStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithGoogleSession = useCallback(async (sessionId) => {
    // Server-side exchange — never call the Emergent session-data endpoint
    // from the browser. Backend validates the domain and mints our JWT.
    const { data } = await api.post('/auth/google-callback', null, {
      headers: { 'X-Session-ID': sessionId },
    });
    sessionStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post('/auth/register', { name, email, password });
    sessionStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('logout request failed:', err?.message);
    }
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, register, logout, refresh, loginWithGoogleSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
